// Webapp configuration

/** App timezone: schedule and event times use Bangkok (Asia/Bangkok, UTC+7). See lib/bangkok-time.ts. */
export const APP_TIMEZONE = "Asia/Bangkok";

/** Environment configuration */
export const ENV_CONFIG = {
  mode: import.meta.env.MODE,
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
  version: import.meta.env.VITE_APP_VERSION ?? "0.0.1",
} as const;

/** Sentry error tracking configuration */
export const SENTRY_CONFIG = {
  /** Sentry DSN (set via VITE_SENTRY_DSN env variable) */
  dsn: import.meta.env.VITE_SENTRY_DSN ?? "",
  /** Enable in development (set VITE_SENTRY_ENABLE_IN_DEV=true) */
  enableInDev: import.meta.env.VITE_SENTRY_ENABLE_IN_DEV === "true",
  /** Error sample rate (0-1) */
  sampleRate: 1.0,
  /** Performance traces sample rate (0-1) */
  tracesSampleRate: ENV_CONFIG.isProd ? 0.1 : 0,
} as const;

/** Short name of the Mini App in Telegram (BotFather → Bot Settings → Menu Button) */
const MINI_APP_SHORT_NAME = "onechurch";

const botUsername = import.meta.env.VITE_BOT_USERNAME ?? "OneChurch_bot";

/** Deep link to open Mini App in Telegram. Set VITE_URL_MINI_APP to override. */
const defaultMiniAppUrl = `https://t.me/${botUsername}/${MINI_APP_SHORT_NAME}`;
const miniAppBaseUrl = import.meta.env.VITE_URL_MINI_APP ?? defaultMiniAppUrl;

/**
 * Get API URL from environment.
 * In production, VITE_API_URL must be set.
 * In development, falls back to localhost:2000.
 */
function getApiUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) return envUrl;

  // In production, require explicit API URL
  if (ENV_CONFIG.isProd) {
    console.error(
      "[Config] VITE_API_URL is not set in production build. " +
        "API requests will fail. Please set VITE_API_URL environment variable."
    );
    // Return empty to make errors obvious rather than silently hitting localhost
    return "";
  }

  // Development fallback
  return "http://localhost:2000";
}

export const config = {
  apiUrl: getApiUrl(),
  botUsername,
  /**
   * Deep link to open the Mini App in Telegram ("Open in Telegram" on login).
   * Format: https://t.me/bot_username/short_name
   */
  miniAppUrl: miniAppBaseUrl,

  /**
   * Deep links to open specific pages in Mini App.
   * Use these in bot messages, menus, etc.
   */
  miniAppDeepLinks: {
    /** Афиша мероприятий (календарь/расписание) */
    schedule: `${miniAppBaseUrl}?startapp=schedule`,
    /** Домашние группы */
    homeGroups: `${miniAppBaseUrl}?startapp=home-groups`,
    /** Добро пожаловать (посадочная страница) */
    welcome: `${miniAppBaseUrl}?startapp=welcome`,
  } as const,

  /**
   * Deep link на конкретное мероприятие.
   * Использовать в рассылках, постах, рекламе события.
   * @param eventId — ID мероприятия (из GraphQL/БД)
   */
  eventDeepLink: (eventId: string) =>
    `${miniAppBaseUrl}?startapp=event_${encodeURIComponent(eventId)}`,

  /**
   * Deep link на страницу заказа (для кнопки в сообщениях пользователю).
   * @param orderId — ID заказа
   */
  orderDeepLink: (orderId: string) =>
    `${miniAppBaseUrl}?startapp=order_${encodeURIComponent(orderId)}`,

  /**
   * Deep link на просмотр розыгрыша.
   * Открывается в мини-аппе Telegram для участников розыгрыша.
   * @param raffleId — ID розыгрыша
   */
  raffleDeepLink: (raffleId: string) =>
    `${miniAppBaseUrl}?startapp=raffle_${encodeURIComponent(raffleId)}`,

  /**
   * Deep link на страницу голосования Gender Party.
   * Открывается в мини-аппе Telegram для гостей мероприятия.
   * @param genderPartyId — ID голосования
   */
  genderPartyDeepLink: (genderPartyId: string) =>
    `${miniAppBaseUrl}?startapp=genderparty_${encodeURIComponent(genderPartyId)}`,

  /**
   * Deep link на страницу Библейской академии.
   * Открывается в мини-аппе Telegram.
   */
  academyDeepLink: `${miniAppBaseUrl}?startapp=academy`,

  /**
   * Deep link на конкретный курс академии.
   * @param courseId — ID курса
   */
  academyCourseDeepLink: (courseId: string) =>
    `${miniAppBaseUrl}?startapp=academy_${encodeURIComponent(courseId)}`,
} as const;
